import path from 'path-browserify';
import { eatOuter, eatInner, ensure_list } from './../imports.js';

export function get_dirname(filePath: string | null): string {
  if (!filePath) return '';
  return path.dirname(filePath);
}
export function get_basename(filePath: string | null): string {
  if (!filePath) return '';
  return path.basename(filePath);
}

export function get_filename(filePath: string) {
  if (!filePath) return '';
  const ext = path.extname(filePath);
  return path.basename(filePath, ext);
}

export function get_extname(filePath: string | null): string {
  if (!filePath) return '';
  return path.extname(filePath);
}

export function get_splitext(filePath: string | null) {
  if (!filePath) return { filename: '', extname: '' };
  const ext = path.extname(filePath);
  // Get the basename without the extension
  const filename = path.basename(filePath, ext);
  return { filename, ext };
}

/**
 * Returns the path of `targetPath` relative to `basePath`.
 * Normalizes separators for consistent web and server environments.
 *
 * @param basePath - The base directory you want to compare from.
 * @param targetPath - The full path of the file/directory.
 * @returns A normalized relative path (e.g., "subdir/file.txt").
 */
export function get_relative_path(basePath: string, targetPath: string): string {
  try {
    // Compute the relative path using Node's native path.relative
    let rel = path.relative(basePath, targetPath);

    // Normalize to POSIX-style slashes for consistency (especially on Windows)
    rel = rel.split(path.sep).join('/');

    // Avoid empty string (happens if both paths are identical)
    return rel || '.';
  } catch (err) {
    console.error(`[get_relative_path] Error computing relative path`, err);
    return targetPath;
  }
}


/**
 * Join multiple path segments — clean, predictable, bulletproof.
 *
 * Accepts ANYTHING that ensure_list can handle:
 *   • 'a,b,c'
 *   • ['a', 'b,c']
 *   • 'a/b/c'
 *   • '/a/', 'b//c'
 *   • mixed arrays, comma strings, whatever
 *
 * Always returns a clean POSIX path string. Never a list.
 */
export function make_path(...parts: any[]): string {
  // Normalize incoming segments into a flat list
  const segments = ensure_list(parts).map(x => String(x));

  let out = "";

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];

    // Normalize a segment:
    let cleaned = eatOuter(seg, "/");   // trim trailing slashes
    

    if (!cleaned) continue;

    if (out === "") {
      out = cleaned;
    } else {
      cleaned = eatInner(cleaned, "/");   // trim leading slashes
      out = `${out}/${cleaned}`;
    }
  }

  return out;
}
export function sanitizeFilename(filename: string): string {
  return filename
    .toLowerCase()
    .replace(/\s+|-/g, '-') // Replace spaces and hyphens with single hyphen
    .replace(/_/g, '-') // Replace underscores with hyphens
    .replace(/[^a-z0-9-.]/g, '') // Remove all non-alphanumeric chars except hyphen and dot
    .replace(/-+/g, '-') // Collapse multiple hyphens into one
    .replace(/^-|-$/, ''); // Remove leading/trailing hyphens
}



export function make_sanitized_path(...paths: string[]): string {
  let real_path = '';
  for (let i = 0; i < paths.length; i++) {
    const sanitized = sanitizeFilename(eatOuter(paths[i], '/'));
    if (i === 0) {
      real_path = sanitized;
    } else if (sanitized) { // Only append if there's a non-empty segment
      real_path = `${real_path}/${sanitized}`;
    }
  }
  return real_path || '';
}

export function normalizeUrl(base: string, p?: string): string {   // ✅ single brace
  if (!p) return base;

  const cleanBase = base.replace(/\/+$/, '');      // regex literal
  const cleanPath = p.replace(/^\/+/, '');

  // collapse multiple “//” into one, but keep the “://” after protocol
  return `${cleanBase}/${cleanPath}`.replace(/([^:])\/{2,}/g, '$1/');
}


export function pathjoin(
	...parts: any[]
	):string {
		return make_path(...parts);
	}


export function pathJoin(
	...parts: any[]
	):string {
		return make_path(...parts);
	}


export function makepath(
	...parts: any[]
	):string {
		return make_path(...parts);
	}


export function makePath(
	...parts: any[]
	):string {
		return make_path(...parts);
	}


export function path_join(
	...parts: any[]
	):string {
		return make_path(...parts);
	}


export function getSplitext(
	filePath: string | null
	) {
		return get_splitext(filePath);
	}


export function getsplitext(
	filePath: string | null
	) {
		return get_splitext(filePath);
	}


export function getextname(
	filePath: string | null
	):string {
		return get_extname(filePath);
	}


export function getExtname(
	filePath: string | null
	):string {
		return get_extname(filePath);
	}


export function getfilename(
	filePath: string
	) {
		return get_filename(filePath);
	}


export function getFilename(
	filePath: string
	) {
		return get_filename(filePath);
	}


export function getbasename(
	filePath: string | null
	):string {
		return get_basename(filePath);
	}


export function getBasename(
	filePath: string | null
	):string {
		return get_basename(filePath);
	}


export function getdirname(
	filePath: string | null
	):string {
		return get_dirname(filePath);
	}


export function getDirname(
	filePath: string | null
	):string {
		return get_dirname(filePath);
	}
